import React, { useState } from 'react';
import { Calendar, Clock, CheckCircle, ArrowRight, Phone, MessageSquare, User, Mail, Lightbulb, Target, Upload, X, FileText, RotateCcw } from 'lucide-react';
import Layout from '../layouts/Layout';
import apiClient from '../helpers/apiClient';
import NotificationPopup from '../components/NotificationPopup';
import { Link } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import projectTypes from '../data/projectTypes';

const ScheduleConsultationPage = () => {
	const { executeRecaptcha } = useGoogleReCaptcha();
	const [selectedDate, setSelectedDate] = useState('');
	const [selectedTime, setSelectedTime] = useState('');
	const [selectedType, setSelectedType] = useState('');
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		company: '',
		phone: '',
		projectType: '',
		goals: ''
	});
	const [uploadedFiles, setUploadedFiles] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [currentStep, setCurrentStep] = useState(1);
	const [dragActive, setDragActive] = useState(false);
	const [agreedToTerms, setAgreedToTerms] = useState(false);

	// Notification state
	const [notification, setNotification] = useState(null);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
	};

	// Notification functions
	const showNotification = (message, type = 'success', duration = 5000) => {
		setNotification({ message, type });

		// Auto-hide after duration
		setTimeout(() => {
			setNotification(null);
		}, duration);
	};

	const hideNotification = () => {
		setNotification(null);
	};

	const validateFile = (file) => {
		const maxSize = 10 * 1024 * 1024; // 10MB
		const allowedTypes = [
			'application/pdf',
			'application/msword',
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
			'text/plain',
			'image/jpeg',
			'image/jpg',
			'image/png',
			'application/vnd.ms-excel',
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		];

		if (file.size > maxSize) {
			return `File "${file.name}" is too large. Maximum size is 10MB.`;
		}

		if (!allowedTypes.includes(file.type)) {
			return `File type "${file.type}" is not allowed. Please use PDF, DOC, TXT, JPG, PNG, or XLS files.`;
		}

		return null;
	};

	const handleFileUpload = (files) => {
		const fileArray = Array.from(files);
		const validFiles = [];
		const errors = [];

		fileArray.forEach(file => {
			const error = validateFile(file);
			if (error) {
				errors.push(error);
			} else {
				// Check if file already exists
				const exists = uploadedFiles.some(existingFile =>
					existingFile.name === file.name && existingFile.size === file.size
				);

				if (!exists) {
					validFiles.push({
						file: file,
						name: file.name,
						size: file.size,
						type: file.type,
						id: Date.now() + Math.random() // Unique ID for deletion
					});
				}
			}
		});

		if (errors.length > 0) {
			showNotification(errors.join('. '), 'error', 8000);
		}

		if (validFiles.length > 0) {
			setUploadedFiles(prev => [...prev, ...validFiles]);
			showNotification(`${validFiles.length} file(s) added successfully`, 'success', 3000);
		}
	};

	const handleFileInputChange = (e) => {
		if (e.target.files && e.target.files.length > 0) {
			handleFileUpload(e.target.files);
			// Reset input so same file can be selected again if needed
			e.target.value = '';
		}
	};

	const removeFile = (fileId) => {
		const fileToRemove = uploadedFiles.find(file => file.id === fileId);
		setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
		if (fileToRemove) {
			showNotification(`"${fileToRemove.name}" removed`, 'info', 2000);
		}
	};

	const handleDrag = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === "dragenter" || e.type === "dragover") {
			setDragActive(true);
		} else if (e.type === "dragleave") {
			setDragActive(false);
		}
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);

		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			handleFileUpload(e.dataTransfer.files);
		}
	};

	const formatFileSize = (bytes) => {
		if (bytes === 0) return '0 B';
		const k = 1024;
		const sizes = ['B', 'KB', 'MB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsSubmitting(true);

		if (!executeRecaptcha) {
			console.log('Execute recaptcha not yet available');
			return;
		}

		try {
			// Execute reCAPTCHA
			const token = await executeRecaptcha('contact_form');

			// Create FormData object for file upload
			const submitData = new FormData();

			// Add consultation data
			submitData.append('consultationType', selectedType);
			submitData.append('selectedDate', selectedDate);
			submitData.append('selectedTime', selectedTime);
			submitData.append('name', formData.name);
			submitData.append('email', formData.email);
			submitData.append('company', formData.company);
			submitData.append('phone', formData.phone);
			submitData.append('projectType', formData.projectType);
			submitData.append('goals', formData.goals);

			// Add reCAPTCHA token
			submitData.append('recaptchaToken', token);

			// Add files
			uploadedFiles.forEach((fileObj) => {
				submitData.append('documents[]', fileObj.file);
			});

			const response = await apiClient.submitConsultationForm(submitData);

			if (response.success) {
				// Success notification
				showNotification(
					`${response.message}`,
					'success',
					6000
				);

				// Mark as successfully submitted
				setIsSubmitted(true);

				// DON'T reset form or go back to step 1
				// Stay on confirmation page to show success state
			} else {
				throw new Error(response.error || 'Form submission failed');
			}

		} catch (error) {
			console.error('Submit error:', error);
			showNotification(
				`Error: ${error.message}`,
				'error',
				8000
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	const nextStep = () => {
		setCurrentStep(prev => Math.min(prev + 1, 4));
	};

	const prevStep = () => {
		setCurrentStep(prev => Math.max(prev - 1, 1));
	};

	const startOver = () => {
		// Reset all form data
		setSelectedDate('');
		setSelectedTime('');
		setSelectedType('');
		setFormData({
			name: '',
			email: '',
			company: '',
			phone: '',
			projectType: '',
			goals: ''
		});
		setUploadedFiles([]);
		setIsSubmitted(false);
		setAgreedToTerms(false);
		setCurrentStep(1);
		showNotification('Form reset. Starting over...', 'info', 3000);
	};

	const consultationTypes = [
		{
			id: 'strategy',
			title: 'Strategy Session',
			duration: '45 minutes',
			icon: <Target className="w-6 h-6" />,
			description: 'Perfect for exploring project scope, technical approach, and strategic planning',
			bestFor: 'New projects, digital transformation, complex requirements'
		},
		{
			id: 'technical',
			title: 'Technical Review',
			duration: '30 minutes',
			icon: <Lightbulb className="w-6 h-6" />,
			description: 'Focus on technical challenges, architecture decisions, and implementation details',
			bestFor: 'Existing projects, technical roadblocks, code reviews'
		},
		{
			id: 'discovery',
			title: 'Discovery Call',
			duration: '60 minutes',
			icon: <MessageSquare className="w-6 h-6" />,
			description: 'Comprehensive discussion about your business, goals, and how we can help',
			bestFor: 'First-time clients, large projects, long-term partnerships'
		}
	];

	const timeFrames = [
		{ id: 'morning', label: 'Morning', time: '09:00 - 11:30', description: 'Start your day with clarity' },
		{ id: 'midday', label: 'Midday', time: '12:00 - 14:30', description: 'Convenient lunch-time slot' },
		{ id: 'afternoon', label: 'Afternoon', time: '15:00 - 17:30', description: 'Perfect for detailed discussions' },
		{ id: 'evening', label: 'Early Evening', time: '18:00 - 19:30', description: 'After regular business hours' }
	];

	// Generate next 10 business days
	const getNextBusinessDays = () => {
		const days = [];
		const today = new Date();
		let currentDate = new Date(today);

		while (days.length < 10) {
			currentDate.setDate(currentDate.getDate() + 1);
			// Skip weekends (0 = Sunday, 6 = Saturday)
			if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
				days.push(new Date(currentDate));
			}
		}

		return days;
	};

	const availableDates = getNextBusinessDays();

	const formatDate = (date) => {
		return date.toLocaleDateString('en-GB', {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	};

	const formatShortDate = (date) => {
		return date.toLocaleDateString('en-GB', {
			weekday: 'short',
			month: 'short',
			day: 'numeric'
		});
	};

	const isStepComplete = (step) => {
		switch (step) {
			case 1: return selectedType !== '';
			case 2: return selectedDate !== '' && selectedTime !== '';
			case 3: return formData.name !== '' && formData.email !== '' && formData.phone !== '' && agreedToTerms;
			case 4: return true; // Step 4 is just confirmation, no additional requirements
			default: return false;
		}
	};

	return (
		<Layout
			title="Schedule Free Consultation - KGC Innovations"
			description="Book a free consultation with KGC Innovations for web development, mobile apps, and digital solutions. Expert advice from UK-based developers. No obligation."
			keywords="free web development consultation UK, schedule developer meeting, book technical consultation, web app development advice, mobile development consultation London"
			path="/schedule-consultation"
		>
			<div className="min-h-screen bg-white">
				{/* Notification Popup */}
				<NotificationPopup notification={notification} hideNotification={hideNotification} />

				{/* Hero Section */}
				<section className="pt-32 pb-16 px-6 bg-gradient-to-br from-slate-50 to-slate-100">
					<div className="max-w-4xl mx-auto text-center">
						<div className="w-20 h-20 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-8">
							<Calendar className="w-10 h-10 text-white" />
						</div>

						<h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
							Schedule Your Free
							<span className="block text-slate-700 mt-2">Consultation</span>
						</h1>

						<p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-3xl mx-auto">
							Book a personalized consultation to discuss your project requirements, explore technical solutions,
							and receive expert guidance from our UK-based development team.
						</p>

						<div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8 border-t border-slate-200">
							<div className="flex items-center justify-center gap-3">
								<CheckCircle className="w-6 h-6 text-green-600" />
								<span className="text-slate-700 font-medium">100% Free Consultation</span>
							</div>
							<div className="flex items-center justify-center gap-3">
								<Clock className="w-6 h-6 text-blue-600" />
								<span className="text-slate-700 font-medium">Flexible Scheduling</span>
							</div>
							<div className="flex items-center justify-center gap-3">
								<Phone className="w-6 h-6 text-purple-600" />
								<span className="text-slate-700 font-medium">Video or Phone Call</span>
							</div>
						</div>
					</div>
				</section>

				{/* Progress Steps */}
				<section className="py-12 bg-white border-b border-slate-200">
					<div className="max-w-4xl mx-auto px-6">
						<div className="flex items-center justify-between">
							{[
								{ step: 1, title: "Choose Type", icon: <Target className="w-5 h-5" /> },
								{ step: 2, title: "Select Time", icon: <Calendar className="w-5 h-5" /> },
								{ step: 3, title: "Your Details", icon: <User className="w-5 h-5" /> },
								{ step: 4, title: "Confirmation", icon: <CheckCircle className="w-5 h-5" /> }
							].map((item, index) => (
								<div key={index} className="flex items-center">
									<div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${currentStep >= item.step
										? 'bg-slate-900 border-slate-900 text-white'
										: 'bg-white border-slate-300 text-slate-400'
										}`}>
										{item.icon}
									</div>
									<div className="ml-4 hidden sm:block">
										<div className={`text-sm font-medium ${currentStep >= item.step ? 'text-slate-900' : 'text-slate-400'
											}`}>
											Step {item.step}
										</div>
										<div className={`text-xs ${currentStep >= item.step ? 'text-slate-600' : 'text-slate-400'
											}`}>
											{item.title}
										</div>
									</div>
									{index < 3 && (
										<div className={`w-8 h-0.5 mx-4 ${currentStep > item.step ? 'bg-slate-900' : 'bg-slate-300'
											}`} />
									)}
								</div>
							))}
						</div>
					</div>
				</section>

				{/* Main Content */}
				<section className="py-16 bg-slate-50">
					<div className="max-w-4xl mx-auto px-6">
						<form onSubmit={handleSubmit}>
							{/* Step 1: Choose Consultation Type */}
							{currentStep === 1 && (
								<div className="bg-white rounded-2xl p-4 sm:p-8 shadow-lg">
									<h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6 text-center">
										Choose Your Consultation Type
									</h2>
									<p className="text-slate-600 text-center mb-8">
										Select the type of consultation that best matches your needs
									</p>

									<div className="grid gap-4 sm:gap-6">
										{consultationTypes.map((type) => (
											<div
												key={type.id}
												className={`border-2 rounded-2xl p-4 sm:p-6 cursor-pointer transition-all hover:shadow-lg ${selectedType === type.id
													? 'border-slate-900 bg-slate-50'
													: 'border-slate-200 hover:border-slate-300'
													}`}
												onClick={() => setSelectedType(type.id)}
											>
												<div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
													<div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${selectedType === type.id
														? 'bg-slate-900 text-white'
														: 'bg-slate-100 text-slate-600'
														}`}>
														{type.icon}
													</div>
													<div className="flex-1 min-w-0">
														<div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3">
															<h3 className="text-lg sm:text-xl font-bold text-slate-900">{type.title}</h3>
															<span className="px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-full w-fit">
																{type.duration}
															</span>
														</div>
														<p className="text-slate-600 mb-3 text-sm sm:text-base">{type.description}</p>
														<p className="text-xs sm:text-sm text-slate-500">
															<strong>Best for:</strong> {type.bestFor}
														</p>
													</div>
													<div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 self-start sm:self-center ${selectedType === type.id
														? 'border-slate-900 bg-slate-900'
														: 'border-slate-300'
														}`}>
														{selectedType === type.id && (
															<CheckCircle className="w-4 h-4 text-white" />
														)}
													</div>
												</div>
											</div>
										))}
									</div>
								</div>
							)}

							{/* Step 2: Select Date & Time */}
							{currentStep === 2 && (
								<div className="bg-white rounded-2xl p-4 sm:p-8 shadow-lg">
									<h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6 text-center">
										Select Date & Time
									</h2>
									<p className="text-slate-600 text-center mb-8">
										Choose a convenient time for your consultation (GMT timezone)
									</p>

									<div className="space-y-8 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-8">
										{/* Date Selection */}
										<div>
											<h3 className="text-lg font-bold text-slate-900 mb-4">Available Dates</h3>
											{/* Desktop: 2 columns, Mobile: horizontal scroll */}
											<div className="hidden sm:grid grid-cols-2 gap-3">
												{availableDates.map((date, index) => (
													<button
														key={index}
														type="button"
														className={`p-3 text-left rounded-lg border-2 transition-all ${selectedDate === date.toISOString().split('T')[0]
															? 'border-slate-900 bg-slate-50'
															: 'border-slate-200 hover:border-slate-300'
															}`}
														onClick={() => setSelectedDate(date.toISOString().split('T')[0])}
													>
														<div className="text-sm font-medium text-slate-900">
															{formatShortDate(date)}
														</div>
														<div className="text-xs text-slate-500">
															{date.toLocaleDateString('en-GB', { year: 'numeric' })}
														</div>
													</button>
												))}
											</div>

											{/* Mobile: horizontal scroll */}
											<div className="sm:hidden">
												<div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide">
													{availableDates.map((date, index) => (
														<button
															key={index}
															type="button"
															className={`flex-shrink-0 p-3 text-center rounded-lg border-2 transition-all min-w-[100px] ${selectedDate === date.toISOString().split('T')[0]
																? 'border-slate-900 bg-slate-50'
																: 'border-slate-200 hover:border-slate-300'
																}`}
															onClick={() => setSelectedDate(date.toISOString().split('T')[0])}
														>
															<div className="text-sm font-medium text-slate-900">
																{formatShortDate(date)}
															</div>
															<div className="text-xs text-slate-500">
																{date.toLocaleDateString('en-GB', { year: 'numeric' })}
															</div>
														</button>
													))}
												</div>
												<p className="text-xs text-slate-500 mt-2">← Scroll to see more dates</p>
											</div>
										</div>

										{/* Time Frame Selection */}
										<div>
											<h3 className="text-lg font-bold text-slate-900 mb-4">Preferred Time</h3>
											<div className="grid grid-cols-1 gap-3">
												{timeFrames.map((timeFrame) => (
													<button
														key={timeFrame.id}
														type="button"
														className={`p-3 sm:p-4 text-left rounded-lg border-2 transition-all ${selectedTime === timeFrame.id
															? 'border-slate-900 bg-slate-50'
															: 'border-slate-200 hover:border-slate-300'
															}`}
														onClick={() => setSelectedTime(timeFrame.id)}
													>
														<div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
															<div className="flex-1">
																<div className="text-sm font-medium text-slate-900">
																	{timeFrame.label}
																</div>
																<div className="text-xs text-slate-500 mb-1">
																	{timeFrame.time} GMT
																</div>
																<div className="text-xs text-slate-400">
																	{timeFrame.description}
																</div>
															</div>
														</div>
													</button>
												))}
											</div>
										</div>
									</div>

									{selectedDate && selectedTime && (
										<div className="mt-8 p-4 bg-slate-50 rounded-lg border border-slate-200">
											<h4 className="font-medium text-slate-900 mb-2">Your Selected Time:</h4>
											<p className="text-slate-700 text-sm sm:text-base">
												{formatDate(new Date(selectedDate))} - {timeFrames.find(tf => tf.id === selectedTime)?.label} ({timeFrames.find(tf => tf.id === selectedTime)?.time} GMT)
											</p>
											<p className="text-sm text-slate-500 mt-1">
												We'll send you a specific time slot within this window based on availability
											</p>
										</div>
									)}
								</div>
							)}

							{/* Step 3: Your Details */}
							{currentStep === 3 && (
								<div className="bg-white rounded-2xl p-8 shadow-lg">
									<h2 className="text-3xl font-bold text-slate-900 mb-6 text-center">
										Your Details
									</h2>
									<p className="text-slate-600 text-center mb-8">
										Tell us about yourself and your project
									</p>

									<div className="space-y-8">
										{/* Contact Information */}
										<div>
											<h3 className="text-xl font-bold text-slate-900 mb-6">Contact Information</h3>
											<div className="grid md:grid-cols-2 gap-6">
												<div>
													<label className="block text-slate-900 font-medium mb-2">
														Full Name *
													</label>
													<input
														type="text"
														name="name"
														value={formData.name}
														onChange={handleInputChange}
														required
														className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
														placeholder="Your full name"
													/>
												</div>
												<div>
													<label className="block text-slate-900 font-medium mb-2">
														Email Address *
													</label>
													<input
														type="email"
														name="email"
														value={formData.email}
														onChange={handleInputChange}
														required
														className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
														placeholder="your@company.com"
													/>
												</div>
												<div>
													<label className="block text-slate-900 font-medium mb-2">
														Company Name
													</label>
													<input
														type="text"
														name="company"
														value={formData.company}
														onChange={handleInputChange}
														className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
														placeholder="Your company"
													/>
												</div>
												<div>
													<label className="block text-slate-900 font-medium mb-2">
														Phone Number *
													</label>
													<input
														type="tel"
														name="phone"
														value={formData.phone}
														onChange={handleInputChange}
														required
														className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
														placeholder="+44 7123 456789"
													/>
												</div>
											</div>
										</div>

										{/* Project Information */}
										<div>
											<h3 className="text-xl font-bold text-slate-900 mb-6">Project Information</h3>
											<div className="space-y-6">
												<div>
													<label className="block text-slate-900 font-medium mb-2">
														Project Type
													</label>
													<select
														name="projectType"
														value={formData.projectType}
														onChange={handleInputChange}
														className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
													>
														<option value="">Select type</option>
														{projectTypes.map((type) => (
															<option key={type} value={type}>{type}</option>
														))}
													</select>
												</div>

												<div>
													<label className="block text-slate-900 font-medium mb-2">
														Project Goals & Requirements
													</label>
													<textarea
														name="goals"
														value={formData.goals}
														onChange={handleInputChange}
														rows={4}
														className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 resize-none"
														placeholder="Briefly describe what you're trying to achieve with this project and any specific requirements you have..."
													/>
												</div>
											</div>
										</div>

										{/* File Upload */}
										<div>
											<h3 className="text-xl font-bold text-slate-900 mb-6">Supporting Documents (Optional)</h3>

											{/* Upload Area */}
											<div
												className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
													? 'border-slate-900 bg-slate-50'
													: 'border-slate-300 hover:border-slate-400'
													}`}
												onDragEnter={handleDrag}
												onDragLeave={handleDrag}
												onDragOver={handleDrag}
												onDrop={handleDrop}
											>
												<input
													type="file"
													id="documents"
													name="documents"
													multiple
													accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.xls,.xlsx"
													onChange={handleFileInputChange}
													className="hidden"
												/>
												<label
													htmlFor="documents"
													className="cursor-pointer flex flex-col items-center"
												>
													<Upload className="w-12 h-12 text-slate-400 mb-4" />
													<span className="text-slate-700 font-medium text-lg mb-2">Upload Project Files</span>
													<span className="text-slate-500 mb-2">
														Drag and drop files here, or click to select
													</span>
													<span className="text-slate-400 text-sm">
														Accepted: PDF, DOC, TXT, JPG, PNG, XLS (max 10MB each)
													</span>
												</label>
											</div>

											{/* Uploaded Files List */}
											{uploadedFiles.length > 0 && (
												<div className="mt-6">
													<div className="text-sm font-medium text-slate-900 mb-4">
														Uploaded files ({uploadedFiles.length}):
													</div>
													<div className="space-y-3">
														{uploadedFiles.map((fileObj) => (
															<div
																key={fileObj.id}
																className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
															>
																<div className="flex items-center flex-1 min-w-0">
																	<FileText className="w-5 h-5 text-slate-500 mr-3 flex-shrink-0" />
																	<div className="min-w-0 flex-1">
																		<div className="text-sm font-medium text-slate-900 truncate">
																			{fileObj.name}
																		</div>
																		<div className="text-xs text-slate-500">
																			{formatFileSize(fileObj.size)}
																		</div>
																	</div>
																</div>
																<button
																	type="button"
																	onClick={() => removeFile(fileObj.id)}
																	className="ml-4 p-1 text-slate-400 hover:text-red-500 transition-colors"
																	title="Remove file"
																>
																	<X className="w-4 h-4" />
																</button>
															</div>
														))}
													</div>
												</div>
											)}
										</div>

										{/* Terms Agreement - Moved from step 4 to step 3 */}
										<div className="pt-6 border-t border-slate-200">
											<label className="flex items-start space-x-3 cursor-pointer">
												<input
													type="checkbox"
													checked={agreedToTerms}
													onChange={(e) => setAgreedToTerms(e.target.checked)}
													required
													className="mt-1 w-4 h-4 text-slate-900 border-slate-300 rounded focus:ring-slate-900 focus:ring-2"
												/>
												<span className="text-sm text-slate-600 leading-relaxed">
													I agree to the{' '}
													<a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline hover:no-underline">
														Privacy Policy
													</a>{' '}
													and{' '}
													<a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline hover:no-underline">
														Terms of Service
													</a>
													. This site is protected by reCAPTCHA and the Google{' '}
													<a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline hover:no-underline">
														Privacy Policy
													</a>{' '}
													and{' '}
													<a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline hover:no-underline">
														Terms of Service
													</a>{' '}
													apply. *
												</span>
											</label>
										</div>
									</div>
								</div>
							)}

							{/* Step 4: Confirmation */}
							{currentStep === 4 && (
								<div className="bg-white rounded-2xl p-8 shadow-lg">
									<h2 className="text-3xl font-bold text-slate-900 mb-6 text-center">
										Confirm Your Consultation
									</h2>
									<p className="text-slate-600 text-center mb-8">
										Please review your details before booking
									</p>

									<div className="space-y-6">
										<div className="bg-slate-50 rounded-lg p-6">
											<h3 className="text-lg font-bold text-slate-900 mb-4">Consultation Details</h3>
											<div className="space-y-3">
												<div className="flex justify-between">
													<span className="text-slate-600">Type:</span>
													<span className="font-medium text-slate-900">
														{consultationTypes.find(t => t.id === selectedType)?.title}
													</span>
												</div>
												<div className="flex justify-between">
													<span className="text-slate-600">Duration:</span>
													<span className="font-medium text-slate-900">
														{consultationTypes.find(t => t.id === selectedType)?.duration}
													</span>
												</div>
												<div className="flex justify-between">
													<span className="text-slate-600">Date:</span>
													<span className="font-medium text-slate-900">
														{formatDate(new Date(selectedDate))}
													</span>
												</div>
												<div className="flex justify-between">
													<span className="text-slate-600">Time:</span>
													<span className="font-medium text-slate-900">
														{timeFrames.find(tf => tf.id === selectedTime)?.label} ({timeFrames.find(tf => tf.id === selectedTime)?.time} GMT)
													</span>
												</div>
											</div>
										</div>

										<div className="bg-slate-50 rounded-lg p-6">
											<h3 className="text-lg font-bold text-slate-900 mb-4">Contact Information</h3>
											<div className="space-y-3">
												<div className="flex justify-between">
													<span className="text-slate-600">Name:</span>
													<span className="font-medium text-slate-900">{formData.name}</span>
												</div>
												<div className="flex justify-between">
													<span className="text-slate-600">Email:</span>
													<span className="font-medium text-slate-900">{formData.email}</span>
												</div>
												{formData.phone && (
													<div className="flex justify-between">
														<span className="text-slate-600">Phone:</span>
														<span className="font-medium text-slate-900">{formData.phone}</span>
													</div>
												)}
												{formData.company && (
													<div className="flex justify-between">
														<span className="text-slate-600">Company:</span>
														<span className="font-medium text-slate-900">{formData.company}</span>
													</div>
												)}
												{formData.projectType && (
													<div className="flex justify-between">
														<span className="text-slate-600">Project Type:</span>
														<span className="font-medium text-slate-900">{formData.projectType}</span>
													</div>
												)}
											</div>
										</div>

										{uploadedFiles.length > 0 && (
											<div className="bg-slate-50 rounded-lg p-6">
												<h3 className="text-lg font-bold text-slate-900 mb-4">Attached Documents</h3>
												<div className="space-y-2">
													{uploadedFiles.map((fileObj) => (
														<div key={fileObj.id} className="flex items-center gap-3 text-sm text-slate-700">
															<FileText className="w-4 h-4 text-slate-500" />
															<span className="truncate">{fileObj.name}</span>
															<span className="text-slate-500">({formatFileSize(fileObj.size)})</span>
														</div>
													))}
												</div>
											</div>
										)}

										<div className="bg-green-50 border border-green-200 rounded-lg p-6">
											<h3 className="text-lg font-bold text-green-900 mb-4">What happens next?</h3>
											<ul className="space-y-2 text-green-800">
												<li className="flex items-start gap-2">
													<CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
													<span>You'll receive a calendar invitation within 30 minutes</span>
												</li>
												<li className="flex items-start gap-2">
													<CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
													<span>We'll send you a preparation guide with suggested topics</span>
												</li>
												{/* <li className="flex items-start gap-2">
													<CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
													<span>Video call link will be included in the calendar invitation</span>
												</li> */}
												<li className="flex items-start gap-2">
													<CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
													<span>You'll receive a follow-up summary with next steps</span>
												</li>
											</ul>
										</div>
									</div>
								</div>
							)}

							{/* Navigation Buttons */}
							<div className="flex justify-between items-center mt-8">
								{/* Left side - Previous/Start Over */}
								{currentStep === 4 ? (
									<button
										type="button"
										onClick={startOver}
										className="px-6 py-3 rounded-lg font-semibold transition-all bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center gap-2"
									>
										<RotateCcw className="w-4 h-4" />
										Start Over
									</button>
								) : (
									<button
										type="button"
										onClick={prevStep}
										disabled={currentStep === 1}
										className={`px-6 py-3 rounded-lg font-semibold transition-all ${currentStep === 1
											? 'bg-gray-100 text-gray-400 cursor-not-allowed'
											: 'bg-slate-100 text-slate-700 hover:bg-slate-200'
											}`}
									>
										Previous
									</button>
								)}

								{/* Center - Step indicator */}
								<div className="flex items-center gap-2">
									<span className="text-sm text-slate-500">
										Step {currentStep} of 4
									</span>
								</div>

								{/* Right side - Next/Submit */}
								{currentStep < 4 ? (
									<button
										type="button"
										onClick={nextStep}
										disabled={!isStepComplete(currentStep)}
										className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${isStepComplete(currentStep)
											? 'bg-slate-900 text-white hover:bg-slate-800'
											: 'bg-gray-100 text-gray-400 cursor-not-allowed'
											}`}
									>
										{currentStep === 3 ? (
											<>Book Consultation <CheckCircle className="w-4 h-4" /></>
										):(
											<>Next <ArrowRight className="w-4 h-4" /></>
										)}
									</button>
								) : (
									!isSubmitted && (
										<button
											type="submit"
											disabled={isSubmitting}
											className="bg-slate-900 text-white px-8 py-3 rounded-lg font-semibold hover:bg-slate-800 transition-colors flex items-center gap-2 disabled:opacity-50"
										>
											{isSubmitting ? (
												<span>Booking...</span>
											) : (
												<>
													Book Consultation <CheckCircle className="w-4 h-4" />
												</>
											)}
										</button>
									)
								)}
							</div>
						</form>
					</div>
				</section>

				{/* What to Expect */}
				<section className="py-16 bg-white">
					<div className="max-w-6xl mx-auto px-6">
						<div className="text-center mb-12">
							<h2 className="text-3xl font-bold text-slate-900 mb-4">What to Expect</h2>
							<p className="text-xl text-slate-600">
								Make the most of your consultation with our structured approach
							</p>
						</div>

						<div className="grid md:grid-cols-3 gap-8">
							<div className="text-center">
								<div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
									<MessageSquare className="w-8 h-8 text-blue-600" />
								</div>
								<h3 className="text-xl font-bold text-slate-900 mb-4">Discovery & Discussion</h3>
								<p className="text-slate-600 leading-relaxed">
									We'll discuss your business goals, technical requirements, and project scope in detail to understand your vision.
								</p>
							</div>

							<div className="text-center">
								<div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
									<Lightbulb className="w-8 h-8 text-green-600" />
								</div>
								<h3 className="text-xl font-bold text-slate-900 mb-4">Expert Recommendations</h3>
								<p className="text-slate-600 leading-relaxed">
									Receive tailored technical advice, architecture suggestions, and best practice recommendations for your project.
								</p>
							</div>

							<div className="text-center">
								<div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
									<ArrowRight className="w-8 h-8 text-purple-600" />
								</div>
								<h3 className="text-xl font-bold text-slate-900 mb-4">Clear Next Steps</h3>
								<p className="text-slate-600 leading-relaxed">
									Walk away with a clear roadmap, timeline estimate, and detailed proposal for moving forward with your project.
								</p>
							</div>
						</div>
					</div>
				</section>

				{/* FAQ */}
				<section className="py-16 bg-slate-50">
					<div className="max-w-4xl mx-auto px-6">
						<div className="text-center mb-12">
							<h2 className="text-3xl font-bold text-slate-900 mb-4">Consultation FAQ</h2>
							<p className="text-xl text-slate-600">
								Common questions about our consultation process
							</p>
						</div>

						<div className="grid md:grid-cols-2 gap-6">
							{[
								{
									question: "Is the consultation really free?",
									answer: "Yes, absolutely! Our initial consultations are completely free with no strings attached. We believe in providing value upfront."
								},
								{
									question: "What if I need to reschedule?",
									answer: "No problem! You can reschedule up to 24 hours before your appointment using the link in your confirmation email."
								},
								{
									question: "Do I need to prepare anything?",
									answer: "We'll send you a preparation guide, but generally just come with your questions and any relevant project materials you'd like to discuss."
								},
								{
									question: "Will I receive a proposal?",
									answer: "Yes! After our consultation, we'll send you a detailed proposal within 24-48 hours outlining our recommendations and next steps."
								},
								{
									question: "Can I bring my team?",
									answer: "Absolutely! We encourage key stakeholders to join the call. Just let us know how many people will be attending."
								},
								{
									question: "What happens after the consultation?",
									answer: "You'll receive a summary of our discussion, a detailed proposal, and clear next steps. There's no pressure to proceed if we're not the right fit."
								}
							].map((faq, index) => (
								<div key={index} className="bg-white rounded-xl p-6 border border-slate-200">
									<h3 className="text-lg font-bold text-slate-900 mb-3">{faq.question}</h3>
									<p className="text-slate-600 leading-relaxed">{faq.answer}</p>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* Contact Alternative */}
				<section className="py-16 bg-white">
					<div className="max-w-4xl mx-auto px-6 text-center">
						<h2 className="text-3xl font-bold text-slate-900 mb-4">Prefer a Different Approach?</h2>
						<p className="text-xl text-slate-600 mb-8">
							Not ready to schedule? We have other ways to get in touch
						</p>

						<div className="grid md:grid-cols-2 gap-6">
							<Link
								to="/contact"
								className="bg-slate-50 border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all hover:-translate-y-1 group"
							>
								<Mail className="w-12 h-12 text-slate-600 mx-auto mb-4 group-hover:scale-110 transition-transform" />
								<h3 className="text-xl font-bold text-slate-900 mb-2">Send Us a Message</h3>
								<p className="text-slate-600">
									Fill out our detailed contact form with your project requirements
								</p>
							</Link>

							<Link
								to="tel:+442070460289"
								className="bg-slate-50 border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all hover:-translate-y-1 group"
							>
								<Phone className="w-12 h-12 text-slate-600 mx-auto mb-4 group-hover:scale-110 transition-transform" />
								<h3 className="text-xl font-bold text-slate-900 mb-2">Call Us Directly</h3>
								<p className="text-slate-600">
									Speak with our team right away: +44 (0) 20 7046 0289
								</p>
							</Link>
						</div>
					</div>
				</section>
			</div>
		</Layout>
	);
};

export default ScheduleConsultationPage;